import React, { useState, useRef, useEffect } from 'react';
import { Image as ImageIcon, Check, X, MousePointer2 } from 'lucide-react';

interface ImageMaskSelectionModalProps {
    isOpen: boolean;
    imageBlob: Blob | null;
    initialPrompt: string;
    onClose: () => void;
    onConfirm: (croppedBlob: Blob, prompt: string, cropRect?: { x: number; y: number; width: number; height: number; imageWidth: number; imageHeight: number; }) => void;
}

export const ImageMaskSelectionModal: React.FC<ImageMaskSelectionModalProps> = ({
    isOpen,
    imageBlob,
    initialPrompt,
    onClose,
    onConfirm
}) => {
    const [prompt, setPrompt] = useState(initialPrompt);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    // Crop state
    const [isDragging, setIsDragging] = useState(false);
    const [dragMode, setDragMode] = useState<'draw' | 'move'>('draw');
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [originalRect, setOriginalRect] = useState<{ x: number, y: number, width: number, height: number } | null>(null);
    const [cropRect, setCropRect] = useState<{ x: number, y: number, width: number, height: number } | null>(null);
    const [hoverMode, setHoverMode] = useState<'draw' | 'move'>('draw');

    // Initialize image
    useEffect(() => {
        if (imageBlob && isOpen) {
            const url = URL.createObjectURL(imageBlob);
            setImageUrl(url);
            setPrompt(initialPrompt);
            setCropRect(null);

            const img = new Image();
            img.onload = () => {
                imageRef.current = img;
                drawCanvas();
            };
            img.src = url;

            return () => {
                URL.revokeObjectURL(url);
                setImageUrl(null);
                imageRef.current = null;
            };
        }
    }, [imageBlob, isOpen]);

    // Update prompt if prop changes while closed
    useEffect(() => {
        if (isOpen) {
            setPrompt(initialPrompt);
        }
    }, [initialPrompt, isOpen]);

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (!canvas || !img) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Make canvas match display size exactly to ensure 1:1 mouse coords
        const container = containerRef.current;
        if (!container) return;

        // Calculate aspect ratio fit
        const containerRatio = container.clientWidth / container.clientHeight;
        const imgRatio = img.width / img.height;

        let drawWidth, drawHeight;
        if (containerRatio > imgRatio) {
            // Fit by height
            drawHeight = container.clientHeight;
            drawWidth = img.width * (drawHeight / img.height);
        } else {
            // Fit by width
            drawWidth = container.clientWidth;
            drawHeight = img.height * (drawWidth / img.width);
        }

        canvas.width = drawWidth;
        canvas.height = drawHeight;

        // Clear and draw image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Draw crop overlay if exists
        if (cropRect) {
            // Darken outside
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            ctx.fillRect(0, 0, canvas.width, cropRect.y); // top
            ctx.fillRect(0, cropRect.y, cropRect.x, cropRect.height); // left
            ctx.fillRect(cropRect.x + cropRect.width, cropRect.y, canvas.width - (cropRect.x + cropRect.width), cropRect.height); // right
            ctx.fillRect(0, cropRect.y + cropRect.height, canvas.width, canvas.height - (cropRect.y + cropRect.height)); // bottom

            // Draw selection border
            ctx.strokeStyle = '#4f46e5'; // indigo-600
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);

            // Draw grid inside (rule of thirds)
            ctx.beginPath();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.lineWidth = 1;
            ctx.setLineDash([]);

            // Vertical lines
            ctx.moveTo(cropRect.x + cropRect.width / 3, cropRect.y);
            ctx.lineTo(cropRect.x + cropRect.width / 3, cropRect.y + cropRect.height);
            ctx.moveTo(cropRect.x + (cropRect.width / 3) * 2, cropRect.y);
            ctx.lineTo(cropRect.x + (cropRect.width / 3) * 2, cropRect.y + cropRect.height);

            // Horizontal lines
            ctx.moveTo(cropRect.x, cropRect.y + cropRect.height / 3);
            ctx.lineTo(cropRect.x + cropRect.width, cropRect.y + cropRect.height / 3);
            ctx.moveTo(cropRect.x, cropRect.y + (cropRect.height / 3) * 2);
            ctx.lineTo(cropRect.x + cropRect.width, cropRect.y + (cropRect.height / 3) * 2);
            ctx.stroke();

            // Corner handles
            ctx.fillStyle = '#4f46e5';
            const handleSize = 6;
            ctx.fillRect(cropRect.x - handleSize / 2, cropRect.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(cropRect.x + cropRect.width - handleSize / 2, cropRect.y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(cropRect.x - handleSize / 2, cropRect.y + cropRect.height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(cropRect.x + cropRect.width - handleSize / 2, cropRect.y + cropRect.height - handleSize / 2, handleSize, handleSize);
        }
    };

    // Redraw whenever cropRect changes
    useEffect(() => {
        drawCanvas();
    }, [cropRect]);

    const getMousePos = (e: React.MouseEvent<HTMLCanvasElement> | MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    };

    const isInsideRect = (pos: { x: number, y: number }, rect: { x: number, y: number, width: number, height: number } | null) => {
        if (!rect) return false;
        return pos.x >= rect.x && pos.x <= rect.x + rect.width && pos.y >= rect.y && pos.y <= rect.y + rect.height;
    };

    const handleMouseMoveCanvas = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDragging) return; // Will be handled by window event listener
        const pos = getMousePos(e);
        if (isInsideRect(pos, cropRect)) {
            setHoverMode('move');
        } else {
            setHoverMode('draw');
        }
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const pos = getMousePos(e);
        setIsDragging(true);
        setStartPos(pos);

        if (isInsideRect(pos, cropRect)) {
            setDragMode('move');
            setOriginalRect(cropRect ? { ...cropRect } : null);
        } else {
            setDragMode('draw');
            setOriginalRect(null);
            setCropRect({ x: pos.x, y: pos.y, width: 0, height: 0 }); // Reset current crop
        }
    };

    // Use window event listeners for smooth drag even outside canvas bounds
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging || !canvasRef.current) return;

            const pos = getMousePos(e);
            const canvas = canvasRef.current;

            if (dragMode === 'draw') {
                // Clamp positions to canvas boundaries
                const currentX = Math.max(0, Math.min(pos.x, canvas.width));
                const currentY = Math.max(0, Math.min(pos.y, canvas.height));

                const originX = Math.max(0, Math.min(startPos.x, canvas.width));
                const originY = Math.max(0, Math.min(startPos.y, canvas.height));

                let width = Math.abs(currentX - originX);
                // Enforce strictly 16:9 aspect ratio (width / height = 16 / 9 => height = width * 9 / 16)
                let height = width * (9 / 16);

                let newX = currentX < originX ? originX - width : originX;
                let newY = currentY < originY ? originY - height : originY;

                // Ensure the calculated 16:9 box doesn't go outside the canvas boundaries
                if (newX + width > canvas.width) {
                    width = canvas.width - newX;
                    height = width * (9 / 16);
                    if (currentY < originY) newY = originY - height; // Adjust upward growth
                }
                if (newY + height > canvas.height) {
                    height = canvas.height - newY;
                    width = height * (16 / 9);
                    if (currentX < originX) newX = originX - width; // Adjust leftward growth
                }

                setCropRect({
                    x: newX,
                    y: newY,
                    width,
                    height
                });
            } else if (dragMode === 'move' && originalRect) {
                const dx = pos.x - startPos.x;
                const dy = pos.y - startPos.y;

                let newX = originalRect.x + dx;
                let newY = originalRect.y + dy;

                // Clamp to canvas edges
                newX = Math.max(0, Math.min(newX, canvas.width - originalRect.width));
                newY = Math.max(0, Math.min(newY, canvas.height - originalRect.height));

                setCropRect({
                    ...originalRect,
                    x: newX,
                    y: newY
                });
            }
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                // Require minimum size
                if (cropRect && (cropRect.width < 50 || cropRect.height < 50)) {
                    setCropRect(null);
                }
            }
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, startPos, cropRect, dragMode, originalRect]);

    const handleConfirm = async () => {
        if (!prompt.trim()) {
            alert("Veuillez entrer un prompt.");
            return;
        }

        if (!cropRect || cropRect.width === 0 || cropRect.height === 0) {
            alert("Veuillez sélectionner une zone (16:9) sur l'image.");
            return;
        }

        const img = imageRef.current;
        const canvas = canvasRef.current;
        if (!img || !canvas) return;

        // We need to map the cropRect (which is in canvas coordinate space) 
        // back to the original image coordinate space to extract full resolution.
        const scaleX = img.width / canvas.width;
        const scaleY = img.height / canvas.height;

        const sourceX = cropRect.x * scaleX;
        const sourceY = cropRect.y * scaleY;
        const sourceWidth = cropRect.width * scaleX;
        const sourceHeight = cropRect.height * scaleY;

        // Create an offscreen canvas for the final export
        const exportCanvas = document.createElement('canvas');
        // We can choose a target resolution (e.g. 1920x1080) or native crop size. 
        // Native is safest to preserve quality without upscaling.
        exportCanvas.width = sourceWidth;
        exportCanvas.height = sourceHeight;

        const exportCtx = exportCanvas.getContext('2d');
        if (!exportCtx) return;

        // Draw cropped area
        exportCtx.drawImage(
            img,
            sourceX, sourceY, sourceWidth, sourceHeight, // Source Rect
            0, 0, sourceWidth, sourceHeight // Dest Rect
        );

        exportCanvas.toBlob((blob) => {
            if (blob) {
                onConfirm(blob, prompt, {
                    x: sourceX,
                    y: sourceY,
                    width: sourceWidth,
                    height: sourceHeight,
                    imageWidth: img.width,
                    imageHeight: img.height
                });
                onClose();
            } else {
                alert("Erreur lors de la création de l'image.");
            }
        }, 'image/jpeg', 0.95);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                            <ImageIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 leading-tight">Zone à modifier (16:9)</h2>
                            <p className="text-sm text-gray-500">Tracez un cadre sur la zone que vous souhaitez transformer</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content / Workspace */}
                <div className="flex-1 bg-gray-100 p-6 flex flex-col gap-6 min-h-0">
                    {/* Viewport for Image */}
                    <div
                        ref={containerRef}
                        className="flex-1 bg-gray-900 rounded-xl overflow-hidden relative shadow-inner ring-1 ring-gray-900/10 flex items-center justify-center cursor-crosshair min-h-[300px]"
                    >
                        {!imageUrl ? (
                            <div className="text-gray-400 flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                <span>Chargement de l'image...</span>
                            </div>
                        ) : (
                            <canvas
                                ref={canvasRef}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMoveCanvas}
                                className={`block max-w-full max-h-full object-contain touch-none shadow-2xl ${hoverMode === 'move' ? 'cursor-move' : 'cursor-crosshair'}`}
                                style={{
                                    // Make sure it doesn't try to stretch
                                    width: canvasRef.current?.width ? `${canvasRef.current.width}px` : 'auto',
                                    height: canvasRef.current?.height ? `${canvasRef.current.height}px` : 'auto'
                                }}
                            />
                        )}

                        {!cropRect && imageUrl && (
                            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center bg-black/40 animate-out fade-out duration-1000 fill-mode-forwards ease-out delay-500">
                                <div className="bg-black/60 backdrop-blur text-white px-4 py-3 rounded-xl flex items-center gap-3 shadow-lg">
                                    <MousePointer2 className="w-5 h-5 animate-pulse" />
                                    <span className="font-medium">Cliquez et glissez pour sélectionner la zone</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Controls Footer */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-3 shrink-0">
                        <label className="text-sm font-semibold text-gray-700 block">
                            Prompt de transformation :
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                            <input
                                type="text"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="ex: Replace the background with a futuristic city..."
                                className="flex-1 px-4 py-3 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && cropRect && prompt.trim()) {
                                        handleConfirm();
                                    }
                                }}
                            />
                            <button
                                onClick={handleConfirm}
                                disabled={!cropRect || !prompt.trim()}
                                className="flex-none flex items-center justify-center gap-2 px-6 py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md active:scale-[0.98]"
                            >
                                <Check className="w-5 h-5" />
                                Modifier
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
